import { AwaitContainer, Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@ibsys/kit';
import { PaletteIcon, PlusIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useSettings } from '@/entities/settings';
import type { ThemeValue } from '@/entities/themes';
import { useThemeParameters, useThemeValues } from '@/entities/themes';
import { ThemeCard } from './ThemeCard';
import { CreateThemeDialog } from './CreateThemeDialog';
import { PageContainer } from '@/shared/ui';

export const ThemesPage: FC = () => {
  const { t } = useTranslation(['common', 'themes']);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [activeTabCode, setActiveTabCode] = useState('');

  const { theme: currentTheme } = useSettings();
  const { data: parametersData, isLoading: isLoadingParameters } = useThemeParameters();
  const { data: valuesData, isLoading: isLoadingValues } = useThemeValues();

  const canEdit = useHasPermission('ADMIN_THEMES');
  const isLoading = isLoadingParameters || isLoadingValues;

  const themes = valuesData?.themes ?? [];
  const colorBlocks = parametersData?.colorParameterBlocks ?? [];

  const defaultTab =
    themes.length > 0 ? (themes.some((theme) => theme.code === currentTheme) ? currentTheme : themes[0].code) : '';

  useEffect(() => {
    if (themes.length > 0) {
      setActiveTabCode((prev) => (prev && themes.some((t) => t.code === prev) ? prev : defaultTab));
    }
  }, [themes, defaultTab]);

  const activeTheme: ThemeValue | undefined = themes.find((t) => t.code === activeTabCode);

  const handleThemeDeleted = useCallback(() => {
    setActiveTabCode('');
  }, []);

  return (
    <PageContainer>
      <AwaitContainer className="min-h-100 w-full" isLoading={isLoading}>
        <div className="flex flex-col gap-6 pb-6">
          {/* Header with project info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PaletteIcon className="size-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-semibold">{t('themes:title')}</h1>
                {valuesData && (
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span>{t('themes:projectNameWithValue', { name: valuesData.projectName })}</span>
                    {valuesData.favicon && (
                      <span className="flex items-center gap-1">
                        {t('themes:faviconWithValue')}
                        <img src={valuesData.favicon} alt="favicon" className="size-4" />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {canEdit && (
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <PlusIcon className="size-4 mr-2" />
                {t('themes:createTheme')}
              </Button>
            )}
          </div>

          {/* Theme tabs */}
          {themes.length > 0 && (
            <Tabs value={activeTabCode} onValueChange={setActiveTabCode} className="w-full">
              <TabsList>
                {themes.map((theme) => (
                  <TabsTrigger key={theme.code} value={theme.code}>
                    {theme.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              {themes.map((theme) => (
                <TabsContent key={theme.code} value={theme.code} className="mt-4">
                  <ThemeCard
                    theme={theme}
                    colorBlocks={colorBlocks}
                    canEdit={canEdit}
                    favicon={valuesData?.favicon}
                    onDeleted={handleThemeDeleted}
                  />
                </TabsContent>
              ))}
            </Tabs>
          )}

          {/* Empty state */}
          {!isLoading && themes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <PaletteIcon className="size-16 mb-4" strokeWidth={1} />
              <p>{t('common:emptyTable')}</p>
            </div>
          )}
        </div>

        {/* Create theme dialog */}
        <CreateThemeDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          colorBlocks={colorBlocks}
          defaultTheme={activeTheme}
        />
      </AwaitContainer>
    </PageContainer>
  );
};
